import type { JSONContent } from "@tiptap/core";

import { httpClient } from "@/lib/httpClient";

/** A page-level SharePoint source link (see SourceDocuments). */
export interface SharepointLink {
  id: string;
  name: string;
  webUrl: string;
}

export interface DraftView {
  /** null = no working draft yet — the view is the published version; the
   * first edit creates the draft (see EditorPage). */
  versionId: string | null;
  pageId: string;
  sectionSlug: string;
  pageSlug: string;
  versionNo: number;
  status: "draft" | "in_review" | "published" | "archived";
  title: string;
  docJson: JSONContent;
  reviewComment: string | null;
  /** Attribution for the sent-back banner: who rejected, and when. */
  reviewedByName: string | null;
  reviewedAt: string | null;
  publishedVersionNo: number | null;
  updatedAt: string;
}

export const editorService = {
  getDraft: (pageId: string) => httpClient.get<DraftView>(`/api/cms/pages/${pageId}/draft`),

  createDraft: (pageId: string, csrfToken: string) =>
    httpClient.post<DraftView>(`/api/cms/pages/${pageId}/draft`, {
      json: {},
      headers: { "x-csrf-token": csrfToken },
    }),

  discardDraft: (versionId: string, csrfToken: string) =>
    httpClient.delete<{ ok: true; pageDeleted: boolean }>(`/api/cms/versions/${versionId}`, {
      headers: { "x-csrf-token": csrfToken },
    }),

  saveDraft: (versionId: string, title: string, docJson: JSONContent, csrfToken: string) =>
    httpClient.put<{ ok: true; savedAt: string }>(`/api/cms/versions/${versionId}/draft`, {
      json: { title, docJson },
      headers: { "x-csrf-token": csrfToken },
    }),

  submit: (versionId: string, changeSummary: string | null, csrfToken: string) =>
    httpClient.post<{ ok: true }>(`/api/cms/versions/${versionId}/submit`, {
      json: { changeSummary },
      headers: { "x-csrf-token": csrfToken },
    }),

  withdraw: (versionId: string, csrfToken: string) =>
    httpClient.post<{ ok: true }>(`/api/cms/versions/${versionId}/withdraw`, {
      json: {},
      headers: { "x-csrf-token": csrfToken },
    }),

  // Page-level, not version-level — a page's SharePoint links exist whether
  // or not it currently has a draft in review.
  getSharepointLinks: (pageId: string) =>
    httpClient.get<{ links: SharepointLink[] }>(`/api/cms/pages/${pageId}/sharepoint-links`),

  addSharepointLink: (pageId: string, webUrl: string, name: string, csrfToken: string) =>
    httpClient.post<{ id: string }>(`/api/cms/pages/${pageId}/sharepoint-links`, {
      json: { webUrl, name },
      headers: { "x-csrf-token": csrfToken },
    }),

  removeSharepointLink: (linkId: string, csrfToken: string) =>
    httpClient.delete<void>(`/api/cms/sharepoint-links/${linkId}`, {
      headers: { "x-csrf-token": csrfToken },
    }),

  uploadAsset: (file: File, pageId: string | null, csrfToken: string) => {
    const fd = new FormData();
    fd.append("file", file);
    if (pageId) fd.append("pageId", pageId);
    return httpClient.postFormJson<{ id: string; url: string; kind: "image" | "file" }>(
      "/api/cms/assets",
      fd,
      { "x-csrf-token": csrfToken }
    );
  },
};
